import { useState, useEffect, ChangeEvent, useMemo } from 'react';
import { apiPublic } from '../api.js';
import { VehicleMake, VehicleType } from '../../vehicles-admin/types.js';
import { ProductType } from '../state/modalStore.js';

// Types
interface VehicleMakesWithTypeId extends VehicleMake {
  type_ids: number[]
}

interface AggregatedModel {
  model_name: string;
  model_slug: string;
  years: number[];
}

interface InitialDataResponse {
  types: VehicleType[]
  makes: VehicleMakesWithTypeId[]
}

export interface SelectedVehicle {
  makeName: string;
  makeSlug: string;
  modelName: string;
  modelSlug: string;
  year: string;
}

interface VehicleSelectionFormProps {
  onVehicleSelect: (vehicle: SelectedVehicle, isClearance: boolean) => void;
  submitButtonText?: string;
  ptype?: ProductType
}

export function VehicleSelectionForm({ onVehicleSelect, ptype, submitButtonText = "Find Parts" }: VehicleSelectionFormProps) {
  const [types, setTypes] = useState<VehicleType[]>([]);
  const [makes, setMakes] = useState<VehicleMakesWithTypeId[]>([]);
  const [models, setModels] = useState<AggregatedModel[]>([]);

  const [isInitialDataLoading, setIsInitialDataLoading] = useState(false);
  const [isModelsLoading, setIsModelsLoading] = useState(false);
  
  const [selectedType, setSelectedType] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModelSlug, setSelectedModelSlug] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const isClearance = (ptype === "clearance");

  useEffect(() => {
    const qs = isClearance ? '?clearance=1' : '';
    setIsInitialDataLoading(true);
    apiPublic<InitialDataResponse>(`/types${qs}`, { }).then(data => {
      setTypes(data.data.types);
      setMakes(data.data.makes);
    }).finally(() => setIsInitialDataLoading(false));
  }, []);

  const filteredMakes = useMemo(() => {
    if (!selectedType) {
      return makes;
    }
    const typeId = parseInt(selectedType, 10);
    return makes.filter(make => make.type_ids.includes(typeId));
  }, [selectedType, makes]);

  const availableYears = useMemo(() => {
    if (!selectedModelSlug) return [];
    const model = models.find(m => m.model_slug === selectedModelSlug);
    return model?.years || [];
  }, [selectedModelSlug, models]);

  const handleTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedType(e.target.value);
    setSelectedMake('');
    setModels([]);
    setSelectedModelSlug('');
    setSelectedYear('');
  };

  const handleMakeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const makeId = e.target.value;
    setSelectedMake(makeId);
    setModels([]);
    setSelectedModelSlug('');
    setSelectedYear('');

    if (makeId) {
      setIsModelsLoading(true);
      const typeParam = selectedType ? `&type_id=${selectedType}` : '';
      const clParam = isClearance ? '&clearance=1' : '';
      apiPublic<AggregatedModel[]>(`/models?make_id=${makeId}${typeParam}${clParam}`).then(data => {
        setModels(data.data);
      }).finally(() => {
        setIsModelsLoading(false);
      });
    }
  };

  const handleModelChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedModelSlug(e.target.value);
    setSelectedYear('');
  };

  const handleYearChange = (e: ChangeEvent<HTMLSelectElement>) => setSelectedYear(e.target.value);

  const handleSubmit = () => {
    const make = makes.find(m => m.make_id.toString() === selectedMake);
    const model = models.find(m => m.model_slug === selectedModelSlug);

    if (make && model && selectedYear) {
      onVehicleSelect({
        makeSlug: make.make_slug,
        makeName: make.make_name,
        modelSlug: selectedModelSlug,
        modelName: model.model_name,
        year: selectedYear,
      }, !!isClearance );
    }
  };

  const isSubmitDisabled = !selectedYear || isInitialDataLoading || isModelsLoading;

  return (
    <div className="gbs-vehicle-selector">
      <div className="gbs-form-grid">
        <div className="gbs-form-field type">
          <label htmlFor="gbs-type-select">Type</label>
          <div className="gbs-select-wrapper">
            <select id="gbs-type-select" value={selectedType} onChange={handleTypeChange} disabled={isInitialDataLoading}>
              <option value="">Type</option>
              {types.map(type => ( <option key={type.type_id} value={type.type_id}>{type.type_name}</option> ))}
            </select>
            {isInitialDataLoading && <div className="gbs-spinner"></div>}
          </div>
        </div>

        <div className="gbs-form-field make">
          <label htmlFor="gbs-make-select">Make</label>
          <div className="gbs-select-wrapper">
            <select id="gbs-make-select" value={selectedMake} onChange={handleMakeChange} disabled={isInitialDataLoading || !selectedType || filteredMakes.length === 0}>
              <option value="">Make</option>
              {filteredMakes.map(make => ( <option key={make.make_id} value={make.make_id}>{make.make_name}</option> ))}
            </select>
          </div>
        </div>

        <div className="gbs-form-field model">
          <label htmlFor="gbs-model-select">Model</label>
          <div className="gbs-select-wrapper">
            <select id="gbs-model-select" value={selectedModelSlug} onChange={handleModelChange} disabled={!selectedMake || isModelsLoading}>
              <option value="">Model</option>
              {models.map(model => ( <option key={model.model_slug} value={model.model_slug}>{model.model_name}</option> ))}
            </select>
            {isModelsLoading && <div className="gbs-spinner"></div>}
          </div>
        </div>

        <div className="gbs-form-field year">
          <label htmlFor="gbs-year-select">Year</label>
          <div className="gbs-select-wrapper">
            <select id="gbs-year-select" value={selectedYear} onChange={handleYearChange} disabled={!selectedModelSlug || availableYears.length === 0}>
              <option value="">Year</option>
              {availableYears.map(year => ( <option key={year || 0} value={year || 0}>{year || "Any Year"}</option> ))}
            </select>
          </div>
        </div>

        <div className="gbs-button-cell">
          <button type="button" className="gbs-button" disabled={isSubmitDisabled} onClick={handleSubmit}>{submitButtonText}</button>
        </div>
      </div>
    </div>
  );
}